import { useCallback, useEffect, useRef, useState } from 'react';
import Daily from '@daily-co/daily-js';

const EVENTS = ['participant-joined', 'participant-updated', 'participant-left', 'track-started', 'track-stopped'];
const friendly = (msg) => (/payment|account/i.test(msg || '') ? 'The video service is not available right now. Please let the hiring team know.'
  : /exp/i.test(msg || '') ? 'This interview room has closed.' : msg);

// Thin wrapper over a Daily call object (custom UI, no Prebuilt iframe).
export function useDailyCall() {
  const ref = useRef(null);
  const [call, setCall] = useState(null);
  const [state, setState] = useState('idle');
  const [participants, setParticipants] = useState({});
  const [speaker, setSpeaker] = useState(null);
  const [error, setError] = useState(null);

  const sync = useCallback(() => { if (ref.current) setParticipants({ ...ref.current.participants() }); }, []);

  const destroy = useCallback(async () => {
    const c = ref.current;
    ref.current = null;
    setCall(null);
    if (!c) return;
    try { await c.leave(); } catch (e) { /* already left */ }
    try { await c.destroy(); } catch (e) { /* already destroyed */ }
  }, []);

  const join = useCallback(async ({ url, token }) => {
    await destroy();
    setError(null);
    setState('joining');
    const c = Daily.createCallObject({ subscribeToTracksAutomatically: true });
    ref.current = c;
    setCall(c);
    EVENTS.forEach((ev) => c.on(ev, sync));
    c.on('joined-meeting', () => { setState('joined'); sync(); });
    c.on('left-meeting', () => setState('left'));
    c.on('active-speaker-change', (e) => setSpeaker(e?.activeSpeaker?.peerId || null));
    c.on('error', (e) => { setError(friendly(e?.errorMsg) || 'The call ended unexpectedly'); setState('error'); });
    c.on('camera-error', () => setError('Your camera or microphone could not be started. Check browser permissions.'));
    try {
      await c.join({ url, token });
    } catch (e) {
      setError(friendly(e?.errorMsg || e?.message) || 'Could not join the call');
      setState('error');
    }
  }, [destroy, sync]);

  const leave = useCallback(async () => { await destroy(); setState('left'); setParticipants({}); }, [destroy]);

  useEffect(() => () => { destroy(); }, [destroy]);

  const local = participants.local;
  const toggleMic = () => call?.setLocalAudio(!call.localAudio());
  const toggleCam = () => call?.setLocalVideo(!call.localVideo());
  const sharing = local?.tracks?.screenVideo?.state === 'playable';
  const toggleScreen = () => (sharing ? call?.stopScreenShare() : call?.startScreenShare());
  return { call, state, participants, speaker, error, join, leave, toggleMic, toggleCam, toggleScreen, sharing,
    micOn: local?.tracks?.audio?.state === 'playable' || local?.tracks?.audio?.state === 'loading',
    camOn: local?.tracks?.video?.state === 'playable' || local?.tracks?.video?.state === 'loading' };
}

export const isCandidate = (p) => (p?.user_id || '').startsWith('cand-');
